import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import {
  CartItemAdditionTypes,
  EventSources,
  EventTypes,
} from '../constants/event';
import { PosProperties } from '../generated-interfaces/graphql';
import {
  CartItem,
  selectModifierInCartItem,
  trackSuppressUpsellOnSelection,
} from '../utils/cart';
import {
  applyUpsellConditionsToCartItem,
  excludedItemHandler,
  expressModeHandler,
  suppressUpsellHandler,
  UpsellConditionHandler,
} from '../utils/cart.upsellConditions';
import { ModSymbolCodeNameMappingType } from '../utils/mappings';
import { GenericMap } from '../utils/types';
import { LOYALTY_APPLY_STATUS } from './cartSlice.constants';
import {
  ICartLoyalty,
  IEntityCartItem,
  ILoyaltyTransmissionMessage,
  initialCartState,
  LoyaltyCouponItem,
  ICartState,
} from './cartSlice.props';
import {
  processCouponResponses,
  processLoyaltyResponses,
} from './cartSlice.utils';
import { selectMenuVersion } from './menuSlice';
import {
  ICheckCouponTransmissionMessage,
  IMessageReceived,
  IStatusTransmissionMessage,
  messagingActions,
  sendLoyalty as sendLoyaltyMessage,
  TransmissionMessage,
} from './messagingSlice';
import { orderActions } from './orderSlice';
import { extractOrderId } from './orderSlice.utils';
import { selectRestaurant, selectStage } from './restaurantSlice';
import logger from '../utils/logger';
import { ISendItemProperties } from './orderSlice.props';
import { CartExpandCollapseState } from '../constants/enums';
import { canShowBMItem } from '../components/ItemBuilder/menu/modGroup.utils';
import {
  IBrandUnavailableEntity,
  IRestaurantUnavailableEntity,
} from '../types/brandMenu';
const expandCollapseCartItems = function (
  state: ICartState,
  cartItemId: number
) {
  Object.keys(state.cartItemCollapseStates).forEach((id) => {
    const numId = parseInt(id);
    if (
      state.cartItemCollapseStates[numId] ===
      CartExpandCollapseState.AUTO_EXPAND
    ) {
      state.cartItemCollapseStates[numId] =
        CartExpandCollapseState.AUTO_COLLAPSE;
    }
  });
  state.cartItemCollapseStates[cartItemId] =
    CartExpandCollapseState.AUTO_EXPAND;
};

const explicitExpandCartItem = function (
  state: ICartState,
  cartItemId: number
) {
  const currentState = state.cartItemCollapseStates[cartItemId];
  if (
    currentState === CartExpandCollapseState.MANUAL_COLLAPSE ||
    currentState === CartExpandCollapseState.AUTO_COLLAPSE
  ) {
    state.cartItemCollapseStates[cartItemId] =
      CartExpandCollapseState.AUTO_EXPAND;
  }
};

export const sendOrderMetrics = createAsyncThunk(
  'cart/sendOrderMetrics',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      cart,
      restaurant: { selectedRestaurantDetails },
      messages: { startFrame },
    } = getState();

    let restaurantCode = selectedRestaurantDetails.restaurantCode || undefined;

    if (!restaurantCode) {
      restaurantCode = startFrame?.data.restaurant_code;
      if (!restaurantCode) {
        throw rejectWithValue('No Current restaurantCode selected!');
      }
    }

    const cartItems = Object.values(cart.cartItems);
    let smallCart: {
      name: string;
      id: string;
      is_upsell?: boolean;
      is_upsell_prompt?: boolean;
      label?: string;
      children: { name: string; id: string }[];
    }[] = [];
    cartItems.forEach((item) => {
      let {
        name,
        id,
        childModifierGroups,
        isUpsell,
        isUpsellPrompt,
        label: parentLabel,
      } = item;
      let groups = Object.values(childModifierGroups);
      let children: {
        name: string;
        id: string;
        is_upsell?: boolean;
        is_upsell_prompt?: boolean;
        label?: string;
      }[] = [];
      groups.forEach((g) => {
        Object.values(g.selectedItems).forEach((child) => {
          let {
            name,
            id,
            isUpsell: is_upsell,
            isUpsellPrompt: is_upsell_prompt,
            label,
          } = child;
          children.push({
            name,
            id,
            ...(is_upsell !== undefined && { is_upsell }),
            ...(is_upsell_prompt !== undefined && { is_upsell_prompt }),
            ...(label && { label }),
          });
        });
      });
      smallCart.push({
        name,
        id,
        children,
        ...(isUpsell !== undefined && { is_upsell: isUpsell }),
        ...(isUpsellPrompt !== undefined && {
          is_upsell_prompt: isUpsellPrompt,
        }),
        ...(parentLabel && { label: parentLabel }),
      });
    });

    const orderMetrics = {
      store_id: restaurantCode,
      items: smallCart,
      source: EventSources.prestoVoice,
      request_id: uuidv4(),
      // session_id: sessionId,
    };

    const payload: Partial<TransmissionMessage & { data: { payload: any } }> = {
      data: { payload: orderMetrics },
      event: EventTypes.info,
    };
    dispatch(messagingActions.sendInfo(payload as any));
  }
);

// Apply Loyalty Code
export const applyLoyaltyCode = createAsyncThunk(
  'cart/applyLoyaltyCode',
  async (
    { loyaltyCode }: { loyaltyCode: string },
    { dispatch, rejectWithValue, getState }
  ) => {
    const rootState = getState();

    let restaurantCode =
      rootState.restaurant.selectedRestaurantDetails.restaurantCode ||
      undefined;
    let currentSessionId = rootState.order.currentSessionId;

    if (!currentSessionId) {
      currentSessionId = extractOrderId(rootState);
      logger.log(
        `Set currentSession in order slice to ${currentSessionId} in applyLoyaltyCode when it's null`
      );
      dispatch(orderActions.setCurrentSession(currentSessionId));
    }

    if (!restaurantCode) {
      restaurantCode = rootState.messages.startFrame?.data.restaurant_code;
      if (!restaurantCode) {
        throw rejectWithValue('No Current restaurantCode selected!');
      }
    }

    const requestId = uuidv4();
    const payload: Partial<ILoyaltyTransmissionMessage> = {
      data: {
        check_id: '-1', // check ID of target check
        loyalty_code: loyaltyCode, // loyalty offer to apply
        session_id: currentSessionId,
        source: EventSources.prestoVoice,
        request_id: requestId,
        store_id: restaurantCode,
        seq_id: '1',
      },
    };

    dispatch(sendLoyaltyMessage(payload as any));
    dispatch(populateLoyaltyRequests({ requestId }));
    dispatch(setCurrentSession(currentSessionId)); // Set currentSessionId in cart slice to be the same with currentSessionId in order slice
    dispatch(setLoyaltyCouponItem(undefined));
    dispatch(setLoyaltyCode(loyaltyCode));
    dispatch(setLoyaltyApplyStatus(LOYALTY_APPLY_STATUS.PROCESSING));
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: initialCartState,
  reducers: {
    populateLoyaltyRequests: (state, { payload: { requestId } }) => {
      state.requests[requestId] = {
        id: requestId,
        status: '',
        transactionId: '',
        loyaltyResponse: null,
        couponResponse: null,
      };
    },
    setCurrentSession: (state, action: PayloadAction<string>) => {
      state.currentSessionId = action.payload;
    },
    addItemToCart: (
      state,
      action: PayloadAction<{
        cartItem: CartItem;
        suppressUpsellsInExpressMode?: boolean;
        suppressUpsellEnabled?: boolean;
      }>
    ) => {
      const {
        cartItem,
        suppressUpsellsInExpressMode = false,
        suppressUpsellEnabled = false,
      } = action.payload;
      const cartItemId = cartItem.cartItemId;
      state.cartItems[cartItemId] = cartItem;
      state.cartItemsQuantity[cartItemId] = 1;
      state.sequenceId++;
      expandCollapseCartItems(state, cartItemId);
      // Skip upsell auto-decline for AI/hypothesis-driven additions — they
      // replay an already-resolved order and shouldn't trigger session-level effects.
      if (cartItem.addedBy !== CartItemAdditionTypes.AI) {
        const handlers: UpsellConditionHandler[] = [
          ...(suppressUpsellEnabled ? [suppressUpsellHandler] : []),
          ...(suppressUpsellsInExpressMode ? [expressModeHandler] : []),
          excludedItemHandler,
        ];
        applyUpsellConditionsToCartItem(
          state.cartItems[cartItemId],
          state.modality,
          handlers
        );
      }
    },
    updateCartItem: (
      state,
      {
        payload: { cartItem, ignoreHypothesis },
      }: PayloadAction<{ cartItem: CartItem; ignoreHypothesis: boolean }>
    ) => {
      state.cartItems[cartItem.cartItemId] = cartItem;
    },
    selectModifier: (
      state,
      {
        payload: {
          cartItemId,
          menuItemId,
          modGroupId,
          selected,
          modCode,
          modSymbolMapping,
          pathToItem,
          rootModGroupId,
          posSettings,
          readback,
          quantity,
          bmItemsSynonyms,
          applySuppressCascade,
          suppressUpsellEnabled = false,
          applyExpressCascade = false,
        },
      }: PayloadAction<{
        cartItemId: number;
        menuItemId: string;
        modGroupId: string;
        selected: boolean;
        modCode: string;
        modSymbolMapping: ModSymbolCodeNameMappingType;
        pathToItem?: string;
        rootModGroupId?: string;
        posSettings: GenericMap<PosProperties>;
        readback?: boolean;
        quantity?: number;
        bmItemsSynonyms?: Record<string, Array<string>>;
        applySuppressCascade?: boolean;
        suppressUpsellEnabled?: boolean;
        applyExpressCascade?: boolean;
      }>
    ) => {
      const { cartItems, modality } = state;
      explicitExpandCartItem(state, cartItemId);
      const cartItem = cartItems[cartItemId];
      const insertedModifier = selectModifierInCartItem(
        cartItem,
        menuItemId,
        modGroupId,
        selected,
        modCode,
        modSymbolMapping,
        posSettings,
        modality,
        pathToItem,
        rootModGroupId,
        readback,
        quantity,
        bmItemsSynonyms
      );
      if (suppressUpsellEnabled) {
        trackSuppressUpsellOnSelection(
          cartItem,
          modGroupId,
          menuItemId,
          selected
        );
      }
      // Scope upsell cascades to the just-inserted modifier — only that
      // subtree could surface new prompts. Skip on deselection (nothing
      // added) or when insertion didn't happen (e.g. max-selections hit).
      if (selected && insertedModifier) {
        const cascadeHandlers: UpsellConditionHandler[] = [
          ...(suppressUpsellEnabled && applySuppressCascade
            ? [suppressUpsellHandler]
            : []),
          ...(applyExpressCascade ? [expressModeHandler] : []),
          excludedItemHandler,
        ];
        if (cascadeHandlers.length > 0) {
          const modGroupName =
            cartItem?.modifierGroups?.[modGroupId]?.name ?? modGroupId;
          logger.log(
            `[UPSELL_CASCADE] selectModifier inserted "${
              insertedModifier.name
            }" into modGroup "${modGroupName}" of cart item "${
              cartItem.name
            }" (cartItemId=${cartItemId}); handlers=[${cascadeHandlers
              .map((h) => h.name)
              .join(', ')}]`
          );
          applyUpsellConditionsToCartItem(
            insertedModifier,
            modality,
            cascadeHandlers,
            [cartItem.name?.toLowerCase()]
          );
        }
      }
    },
    applyOptionReplacement: (
      state,
      {
        payload: { cartItemId, targetModifierGroups, unavailableItemsByName },
      }: PayloadAction<{
        cartItemId: number;
        targetModifierGroups: Array<{
          modifierGroupName: string;
          optionName: string;
        }>;
        unavailableItemsByName: Record<
          string,
          Array<IRestaurantUnavailableEntity | IBrandUnavailableEntity>
        >;
      }>
    ) => {
      const { cartItems } = state;
      const cartItem = cartItems[cartItemId];

      if (!cartItem) {
        logger.log({
          message: `Can't apply option replacement due to cart item ${cartItemId} not found for option replacement`,
          targetModifierGroups: JSON.stringify(targetModifierGroups),
        });
        return;
      }

      // Apply option replacement to each target modifier group
      targetModifierGroups.forEach(({ modifierGroupName, optionName }) => {
        // Find the modifier group in the cart item's available modifier groups
        const modifierGroup = Object.values(cartItem.modifierGroups || {}).find(
          (group) => group.name === modifierGroupName
        );

        const canShowItem = canShowBMItem({
          unavailableItemsByName,
          itemName: optionName,
          cartItem,
          modGroupName: modifierGroupName,
        });

        if (modifierGroup && canShowItem) {
          // Find the menu item that matches the option name
          const menuItem = Object.values(modifierGroup.menuItems || {}).find(
            (item) => item.name === optionName
          );

          if (menuItem) {
            // Select the modifier
            selectModifierInCartItem(
              cartItem,
              menuItem.itemId,
              modifierGroup.id,
              true, // selected
              '', // modCode
              {}, // modSymbolMapping
              {}, // posSettings
              state.modality,
              '', // pathToItem
              '', // rootModGroupId
              true, // readback
              undefined, // quantity - don't pass quantity to avoid adding to existing
              {} // bmItemsSynonyms
            );

            logger.log(
              `Applied option replacement: ${optionName} in ${modifierGroupName} for cart item ${cartItem.name}`
            );
          } else {
            logger.log(
              `Menu item ${optionName} not found in modifier group ${modifierGroupName} for cart item ${cartItem.name}`
            );
          }
        } else if (!modifierGroup) {
          logger.log(
            `Modifier group ${modifierGroupName} not found for cart item ${cartItem.name}`
          );
        } else if (!canShowItem) {
          logger.log(
            `Option ${optionName} is not available in modifier group ${modifierGroupName} for cart item ${cartItem.name}`
          );
        }
      });
    },
    cloneCartItem: (
      state,
      { payload }: PayloadAction<{ id: string; quantity: number }>
    ) => {
      const { id, quantity } = payload;
      state.sequenceId += 1;
      const cartItems = {
        ...state.cartItems,
        [state.sequenceId]: {
          ...state.cartItems[id],
          cartItemId: state.sequenceId,
          isConverted: false, // Duplicated items should not have the converted flag
        },
      };
      expandCollapseCartItems(state, state.sequenceId);
      state.cartItems = { ...cartItems };
      state.cartItemsQuantity[state.sequenceId] = quantity;
      state.sequenceId += 1;
    },
    deleteCartItem: (state, { payload }: PayloadAction<string>) => {
      delete state.cartItems[payload];
      delete state.cartItemsQuantity[payload];
      delete state.cartItemCollapseStates[parseInt(payload)];
    },
    clearCart: () => {
      return Object.assign({}, initialCartState);
    },
    setLoyaltyApplyStatus: (
      state,
      { payload }: PayloadAction<LOYALTY_APPLY_STATUS | undefined>
    ) => {
      state.loyaltyApplyStatus = payload;
    },
    updateQuantity: (
      state,
      { payload }: PayloadAction<{ cartItemId: string; quantity: number }>
    ) => {
      if (payload.quantity > 0) {
        if (state.cartItemsQuantity[payload.cartItemId] === payload.quantity) {
          // For brand menu auto-combo non-combined item, we will still send the same quantity
          state.cartItemsQuantity = {
            ...state.cartItemsQuantity,
            [payload.cartItemId]: payload.quantity,
          };
        } else {
          state.cartItemsQuantity[payload.cartItemId] = payload.quantity;
        }
      } else {
        delete state.cartItems[payload.cartItemId];
        delete state.cartItemsQuantity[payload.cartItemId];
      }
    },
    setLoyaltyCode: (state, { payload }: PayloadAction<string>) => {
      state.loyaltyCode = payload;
    },
    setLoyaltyCouponItem: (
      state,
      { payload }: PayloadAction<LoyaltyCouponItem | undefined>
    ) => {
      state.loyaltyCouponItem = payload;
    },
    addCouponToCart: (state, { payload }: PayloadAction<string>) => {
      const id = uuidv4();
      state.coupons[id] = {
        id,
        code: payload,
      };
    },
    deleteCoupon: (state, { payload }: PayloadAction<string>) => {
      delete state.coupons[payload];
    },
    addHypothesisCartItem: (
      state,
      { payload }: PayloadAction<IEntityCartItem>
    ) => {
      state.hypothesisCartItems[payload.cartItemId] = payload;
      state.cartItemsQuantity[payload.cartItemId] = payload.quantity;
      state.sequenceId++;
    },
    deleteHypothesisItem: (state, { payload }: PayloadAction<string>) => {
      delete state.hypothesisCartItems[payload];
      delete state.cartItemsQuantity[payload];
    },
    setSequenceId: (state, { payload }: PayloadAction<number>) => {
      state.sequenceId = payload;
    },
    setPosCartItems: (
      state,
      { payload }: PayloadAction<ISendItemProperties[]>
    ) => {
      state.posCartItems = payload;
    },
    addLoyaltiesCode: (state, action: PayloadAction<ICartLoyalty>) => {
      const { code, addedBy = CartItemAdditionTypes.human } = action.payload;
      // Maintaining only one loyalty code
      state.loyalties = [{ code, addedBy }];
    },
    removeLoyaltiesCode: (state, action: PayloadAction<string>) => {
      state.loyalties = state.loyalties.filter(
        ({ code }) => code !== action.payload
      );
    },
    resetLoyaltiesCode: (state) => {
      state.loyalties = [];
    },
    setCartItemCollapseState: (
      state,
      { payload }: PayloadAction<{ cartItemId: number }>
    ) => {
      const { cartItemId } = payload;
      const currentState = state.cartItemCollapseStates[cartItemId];
      let newState = CartExpandCollapseState.MANUAL_EXPAND;
      if (
        currentState === CartExpandCollapseState.AUTO_EXPAND ||
        currentState === CartExpandCollapseState.MANUAL_EXPAND
      ) {
        newState = CartExpandCollapseState.MANUAL_COLLAPSE;
      }
      state.cartItemCollapseStates[cartItemId] = newState;
    },
    setExplicitExpandState: (
      state,
      { payload }: PayloadAction<{ cartItemId: number }>
    ) => {
      const { cartItemId } = payload;
      explicitExpandCartItem(state, cartItemId);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(selectRestaurant.fulfilled, () => {
      return Object.assign({}, initialCartState);
    });
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const { messages } = action.payload;
        messages.forEach((message) => {
          if (message.session_id === state.currentSessionId) {
            if (message.event === EventTypes.coupon) {
              const { data } = message as ICheckCouponTransmissionMessage;
              const { request_id: requestId = '' } = data || {};

              if (state.requests[requestId]) {
                state.requests[requestId].couponResponse = message;
                processCouponResponses(state, requestId);
              }
            } else if (message.event === EventTypes.loyaltyStatus) {
              const { data: { request_id: requestId = '' } = {} } =
                message as IStatusTransmissionMessage;

              if (state.requests[requestId]) {
                state.requests[requestId].loyaltyResponse = message;
                processLoyaltyResponses(state, requestId);
              }
            }
          }
        });
      }
    );
    builder.addMatcher(
      isAnyOf(
        selectRestaurant.fulfilled,
        selectStage.fulfilled,
        selectMenuVersion.fulfilled
      ),
      (state, action) => {
        const newState = Object.assign({}, initialCartState);
        state = newState;
        return state;
      }
    );
  },
});

export const cartActions = cartSlice.actions;
export const {
  addItemToCart,
  updateCartItem,
  selectModifier,
  applyOptionReplacement,
  cloneCartItem,
  deleteCartItem,
  clearCart,
  updateQuantity,
  populateLoyaltyRequests,
  setCurrentSession,
  setLoyaltyApplyStatus,
  setLoyaltyCode,
  setLoyaltyCouponItem,
  addCouponToCart,
  deleteCoupon,
  addHypothesisCartItem,
  deleteHypothesisItem,
  setSequenceId,
  setPosCartItems,
  addLoyaltiesCode,
  removeLoyaltiesCode,
  resetLoyaltiesCode,
  setCartItemCollapseState,
  setExplicitExpandState,
} = cartSlice.actions;
export default cartSlice.reducer;
